/**
 * Guards against unsubstituted Octopus deployment placeholders.
 *
 * Octopus substitutes #{VarName} tokens into .env files at deploy time.
 * If a variable is missing from the Octopus project, the raw placeholder
 * reaches the running server. This function normalises such values to an
 * empty string so that consumers receive a safe falsy-equivalent instead
 * of a broken #{...} string (e.g. as a script src or API URL).
 *
 * @example
 * resolveEnvVar("https://imgix.example.com")  // → "https://imgix.example.com"
 * resolveEnvVar("#{ImgixEndpoint}")            // → ""
 * resolveEnvVar(undefined)                     // → ""
 */
export function resolveEnvVar(value: string | undefined): string {
    if (!value) {
        return "";
    }

    // Octopus placeholders: #{VariableName} — treat as unset
    if (/^#\{.+\}$/.test(value)) {
        return "";
    }

    return value;
}

/**
 * Reads an environment variable at runtime without Next.js inlining.
 *
 * Next.js statically replaces `process.env.NEXT_PUBLIC_*` with string
 * literals at build time. In containerised deployments the real values
 * are only available at runtime (substituted by Octopus into
 * `.env.production`). Using dynamic bracket access (`process.env[key]`)
 * prevents the compiler from inlining the value.
 *
 * Also applies the Octopus-placeholder guard via {@link resolveEnvVar}.
 */
export function getEnvVar(key: string): string {
    return resolveEnvVar(process.env[key]);
}
